import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";
const api = axios.create({ baseURL: API_URL });

const MONTHS = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];

export default function PublicPayment() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const [selectedInvoices, setSelectedInvoices] = useState([]); // seçili fatura id'leri
  const [step, setStep] = useState("summary"); // summary | form | success
  const [paying, setPaying] = useState(false);
  const [payResult, setPayResult] = useState(null);

  const [card, setCard] = useState({ holder: "", number: "", expiry: "", cvv: "" });
  const [cardError, setCardError] = useState("");

  useEffect(() => {
    fetchDebt();
  }, [token]);

  const fetchDebt = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/payment-links/${token}`);
      setData(res.data.data);
      // Tüm faturalar varsayılan seçili
      setSelectedInvoices(res.data.data.items.map(i => i.invoice_id));
    } catch (e) {
      setError(e.response?.data?.message || "Ödeme linki geçersiz veya süresi dolmuş.");
    } finally {
      setLoading(false);
    }
  };

  const toggleInvoice = (id) => {
    setSelectedInvoices(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectedTotal = data?.items
    .filter(i => selectedInvoices.includes(i.invoice_id))
    .reduce((s, i) => s + parseFloat(i.total_amount), 0) || 0;

  const formatCardNumber = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  const formatExpiry = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 2) return digits.slice(0, 2) + "/" + digits.slice(2);
    return digits;
  };

  const validateCard = () => {
    const num = card.number.replace(/\s/g, "");
    if (!card.holder.trim()) return "Kart sahibi adı zorunlu";
    if (num.length !== 16) return "Kart numarası 16 haneli olmalı";
    if (card.expiry.length !== 5) return "Son kullanma tarihi geçersiz (AA/YY)";
    if (card.cvv.length < 3) return "CVV geçersiz";
    return "";
  };

  const handlePay = async () => {
    const err = validateCard();
    if (err) { setCardError(err); return; }
    if (selectedInvoices.length === 0) { setCardError("En az bir fatura seçin"); return; }

    setPaying(true); setCardError("");
    try {
      const [month, year] = card.expiry.split("/");
      const res = await api.post(`/payment-links/${token}/pay`, {
        card_holder: card.holder,
        card_number: card.number.replace(/\s/g, ""),
        expiry_month: month,
        expiry_year: year,
        cvv: card.cvv,
        invoice_ids: selectedInvoices
      });
      setPayResult(res.data);
      setStep("success");
    } catch (e) {
      setCardError(e.response?.data?.message || "Ödeme işlemi başarısız. Lütfen tekrar deneyin.");
    } finally {
      setPaying(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-gray-500 text-sm">Yükleniyor...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="text-5xl mb-4">⚠️</div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Link Geçersiz</h2>
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    </div>
  );

  // BAŞARILI ÖDEME
  if (step === "success") return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Ödeme Başarılı!</h2>
        <p className="text-gray-500 mb-4">{data?.owner_name} — Daire {data?.unit_number}</p>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
          <p className="text-green-800 font-bold text-2xl">₺{parseFloat(payResult?.total_paid || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</p>
          <p className="text-green-600 text-sm mt-1">{payResult?.paid_invoices?.length} fatura ödendi</p>
        </div>
        <p className="text-xs text-gray-400">Ödemeniz sisteme işlenmiştir. Teşekkür ederiz.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Aidat Ödeme</h1>
          <p className="text-gray-500 text-sm mt-1">{data.tenant_name} — Daire {data.unit_number} — {data.owner_name}</p>
        </div>

        {/* BORÇ ÖZETİ */}
        {step === "summary" && (
          <>
            {data.items.length === 0 ? (
              <div className="bg-white rounded-2xl shadow p-8 text-center">
                <div className="text-4xl mb-3">✅</div>
                <h2 className="font-bold text-gray-800 text-lg">Borcunuz bulunmuyor</h2>
                <p className="text-gray-500 text-sm mt-1">Tüm faturalarınız ödenmiş.</p>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-2xl shadow overflow-hidden mb-4">
                  <div className="px-5 py-4 border-b bg-red-50">
                    <h2 className="font-bold text-red-800">📋 Ödenmemiş Faturalar</h2>
                    <p className="text-xs text-red-600 mt-0.5">Ödemek istediğiniz faturaları seçin</p>
                  </div>
                  <div className="divide-y">
                    {data.items.map(item => (
                      <label key={item.invoice_id} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedInvoices.includes(item.invoice_id)}
                          onChange={() => toggleInvoice(item.invoice_id)}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-800">
                              {MONTHS[item.period_month - 1]} {item.period_year}
                            </span>
                            {item.months_overdue > 0 && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                                {item.months_overdue} ay gecikmiş
                              </span>
                            )}
                            {item.is_period_exempt && (
                              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                                📅 Faiz muaf
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 flex gap-3">
                            <span>Aidat: ₺{parseFloat(item.effective_base).toFixed(2)}</span>
                            {parseFloat(item.fee_amount) > 0 && (
                              <span className="text-orange-600">+ Faiz: ₺{parseFloat(item.fee_amount).toFixed(2)}</span>
                            )}
                            {parseFloat(item.exempted_fee) > 0 && (
                              <span className="text-purple-500 line-through">Faiz: ₺{parseFloat(item.exempted_fee).toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                        <span className="font-bold text-gray-800">₺{parseFloat(item.total_amount).toFixed(2)}</span>
                      </label>
                    ))}
                  </div>

                  {/* Özet */}
                  <div className="px-5 py-4 bg-gray-50 border-t space-y-1.5">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Seçili fatura</span>
                      <span>{selectedInvoices.length} adet</span>
                    </div>
                    {data.summary.total_exempted_fee > 0 && (
                      <div className="flex justify-between text-sm text-purple-500">
                        <span>Muafiyet indirimi</span>
                        <span className="line-through">-₺{parseFloat(data.summary.total_exempted_fee).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-bold text-gray-900 border-t pt-2">
                      <span>Ödenecek Toplam</span>
                      <span className="text-red-700">₺{selectedTotal.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setStep("form")}
                  disabled={selectedInvoices.length === 0}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white font-bold py-4 rounded-2xl text-lg shadow"
                >
                  Ödemeye Geç →
                </button>
              </>
            )}
          </>
        )}

        {/* KART FORMU */}
        {step === "form" && (
          <div className="bg-white rounded-2xl shadow overflow-hidden">
            <div className="px-5 py-4 border-b bg-blue-50">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-blue-800">💳 Kart Bilgileri</h2>
                <button onClick={() => setStep("summary")} className="text-sm text-blue-600 hover:underline">← Geri</button>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Ödenecek toplam: <strong>₺{selectedTotal.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</strong>
                {" "}({selectedInvoices.length} fatura)
              </p>
            </div>

            <div className="p-5 space-y-4">
              {cardError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
                  {cardError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Kart Üzerindeki Ad Soyad</label>
                <input
                  type="text"
                  value={card.holder}
                  onChange={e => setCard({...card, holder: e.target.value.toUpperCase()})}
                  placeholder="AD SOYAD"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 tracking-wider"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Kart Numarası</label>
                <input
                  type="text"
                  value={card.number}
                  onChange={e => setCard({...card, number: formatCardNumber(e.target.value)})}
                  placeholder="0000 0000 0000 0000"
                  maxLength={19}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 tracking-widest font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Son Kullanma</label>
                  <input
                    type="text"
                    value={card.expiry}
                    onChange={e => setCard({...card, expiry: formatExpiry(e.target.value)})}
                    placeholder="AA/YY"
                    maxLength={5}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono tracking-widest"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">CVV</label>
                  <input
                    type="password"
                    value={card.cvv}
                    onChange={e => setCard({...card, cvv: e.target.value.replace(/\D/g, "").slice(0, 4)})}
                    placeholder="•••"
                    maxLength={4}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                  />
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 flex gap-2">
                <span>🔒</span>
                <span>Kart bilgileriniz güvenli şekilde işlenmektedir. Bilgileriniz sunucularımızda saklanmaz.</span>
              </div>

              <button
                onClick={handlePay}
                disabled={paying}
                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-4 rounded-2xl text-lg shadow flex items-center justify-center gap-2"
              >
                {paying ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Ödeme yapılıyor...
                  </>
                ) : (
                  <>💳 ₺{selectedTotal.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} Öde</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}